import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
  BorderStyle, Table, TableRow, TableCell, WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ── HTML → docx paragraphs ────────────────────────────────────
function htmlToDocxParagraphs(html: string): Paragraph[] {
  if (!html || html === '<p></p>') return [];
  const div = document.createElement('div');
  div.innerHTML = html;
  const paras: Paragraph[] = [];

  const nodeToRuns = (el: Element): TextRun[] => {
    const runs: TextRun[] = [];
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text) runs.push(new TextRun({ text }));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const child = node as Element;
        const tag = child.tagName.toLowerCase();
        const inner = child.textContent || '';
        if (!inner) return;
        runs.push(new TextRun({
          text: inner,
          bold: tag === 'strong' || tag === 'b',
          italics: tag === 'em' || tag === 'i',
          underline: tag === 'u' ? {} : undefined,
          strike: tag === 's',
        }));
      }
    });
    return runs;
  };

  div.childNodes.forEach(node => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const text = el.textContent || '';

    if (tag === 'h1') {
      paras.push(new Paragraph({ text, heading: HeadingLevel.HEADING_1, spacing: { before: 240, after: 120 } }));
    } else if (tag === 'h2') {
      paras.push(new Paragraph({ text, heading: HeadingLevel.HEADING_2, spacing: { before: 200, after: 100 } }));
    } else if (tag === 'h3') {
      paras.push(new Paragraph({ text, heading: HeadingLevel.HEADING_3, spacing: { before: 160, after: 80 } }));
    } else if (tag === 'p') {
      const runs = nodeToRuns(el);
      if (runs.length > 0) paras.push(new Paragraph({ children: runs, spacing: { after: 120 } }));
    } else if (tag === 'ul' || tag === 'ol') {
      el.querySelectorAll('li').forEach((li, idx) => {
        const bullet = tag === 'ul' ? '•' : `${idx + 1}.`;
        paras.push(new Paragraph({
          children: [new TextRun({ text: `${bullet}  ${li.textContent || ''}` })],
          spacing: { after: 60 }, indent: { left: 360 },
        }));
      });
    } else if (tag === 'blockquote') {
      paras.push(new Paragraph({
        children: [new TextRun({ text: text, italics: true, color: '6B7280' })],
        indent: { left: 720 }, spacing: { after: 120 },
      }));
    } else if (text.trim()) {
      paras.push(new Paragraph({ children: nodeToRuns(el), spacing: { after: 120 } }));
    }
  });

  return paras;
}

async function generateDeliverableDocx(deliverable: any): Promise<void> {
  const sections: any[] = deliverable.sections || [];
  const children: Paragraph[] = [];

  // Title
  children.push(new Paragraph({
    children: [new TextRun({ text: deliverable.title, bold: true, size: 56, color: '1A1A2E' })],
    heading: HeadingLevel.TITLE,
    spacing: { after: 200 },
  }));

  // Metadata row
  const locale = deliverable._locale || 'el-GR';
  const meta: string[] = [];
  if (deliverable.category) meta.push(`${deliverable._catLabel || 'Κατηγορία'}: ${deliverable.category}`);
  if (deliverable.deadline) meta.push(`${deliverable._deadlineLabel || 'Προθεσμία'}: ${new Date(deliverable.deadline).toLocaleDateString(locale)}`);
  if (deliverable.status) meta.push(`Status: ${deliverable.status}`);
  if (meta.length) {
    children.push(new Paragraph({
      children: [new TextRun({ text: meta.join('   |   '), color: '6B7280', size: 20 })],
      spacing: { after: 400 },
    }));
  }
  if (deliverable.description) {
    children.push(new Paragraph({
      children: [new TextRun({ text: deliverable.description, italics: true, color: '374151' })],
      spacing: { after: 400 },
    }));
  }

  // Chapters
  sections.forEach((sec: any, si: number) => {
    // Chapter heading
    children.push(new Paragraph({
      children: [new TextRun({ text: `${si + 1}. ${sec.title}`, bold: true, size: 36, color: '1A1A2E' })],
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 160 },
    }));

    // Chapter content
    if (sec.content && sec.content !== '<p></p>') {
      htmlToDocxParagraphs(sec.content).forEach(p => children.push(p));
    }

    // Subsections
    (sec.subsections || []).forEach((sub: any, ssi: number) => {
      children.push(new Paragraph({
        children: [new TextRun({ text: `${si + 1}.${ssi + 1} ${sub.title}`, bold: true, size: 28, color: '374151' })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 280, after: 120 },
      }));
      if (sub.content && sub.content !== '<p></p>') {
        htmlToDocxParagraphs(sub.content).forEach(p => children.push(p));
      }
    });
  });

  const doc = new Document({
    creator: 'eazyproject',
    title: deliverable.title,
    sections: [{ children }],
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 24, color: '1A1A2E' } },
      },
    },
  });

  const blob = await Packer.toBlob(doc);
  const safeName = deliverable.title.replace(/[/\\:*?"<>|]/g, '').trim() || 'deliverable';
  saveAs(blob, `${safeName}.docx`);
}

// ── Types ─────────────────────────────────────────────────────
interface SubSection { id: string; title: string; content: string; }
interface Section { id: string; title: string; content: string; subsections: SubSection[]; }
interface Deliverable {
  id?: string; title: string; description?: string; category?: string;
  deadline?: string; status?: string; sections: Section[];
  last_edited_by_name?: string; last_edited_at?: string;
}
interface OtherDoc {
  id: string; file_name: string; file_path?: string; file_size?: number;
  file_type?: string; category?: string; uploaded_by_name?: string; created_at: string;
  has_knowledge?: boolean;
}
interface SelectedItem { sectionId: string; subsectionId?: string; }

// ── Rich text toolbar ─────────────────────────────────────────
const Toolbar: React.FC<{ editor: any }> = ({ editor }) => {
  const { language } = useLanguage();
  if (!editor) return null;
  const btn = (active: boolean, onClick: () => void, label: string, title?: string) => (
    <button
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title || label}
      style={{
        padding: '4px 8px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
        backgroundColor: active ? '#e0e7ff' : 'transparent',
        color: active ? '#4338ca' : '#374151',
        fontWeight: active ? 700 : 400, minWidth: 28,
      }}
    >{label}</button>
  );
  const sep = () => <div style={{ width: 1, height: 20, backgroundColor: '#e5e7eb', margin: '0 4px' }} />;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
      padding: '8px 12px', borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#fafafa', borderRadius: '10px 10px 0 0',
    }}>
      {btn(false, () => editor.chain().focus().undo().run(), '↩', 'Αναίρεση')}
      {btn(false, () => editor.chain().focus().redo().run(), '↪', 'Επανάληψη')}
      {sep()}
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'H1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'H3')}
      {btn(editor.isActive('paragraph'), () => editor.chain().focus().setParagraph().run(), 'P', 'Παράγραφος')}
      {sep()}
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'B')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'I')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'U')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), 'S̶')}
      {sep()}
      {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), '⬤ ≡', 'Αριστερά')}
      {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), '≡ ≡', 'Κεντρικά')}
      {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), '≡ ⬤', 'Δεξιά')}
      {btn(editor.isActive({ textAlign: 'justify' }), () => editor.chain().focus().setTextAlign('justify').run(), '⬜', 'Πλήρης στοίχιση')}
      {sep()}
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), '• —', 'Μη αριθμημένη λίστα')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), '1. —', 'Αριθμημένη λίστα')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), '❝', 'Παράθεση')}
      {sep()}
      {btn(editor.isActive('code'), () => editor.chain().focus().toggleCode().run(), '<>', 'Κώδικας inline')}
      {btn(editor.isActive('codeBlock'), () => editor.chain().focus().toggleCodeBlock().run(), '⌨', 'Μπλοκ κώδικα')}
    </div>
  );
};

// ── Create/Edit Deliverable Modal ─────────────────────────────
const DeliverableModal: React.FC<{
  initial?: Deliverable;
  onSave: (d: Deliverable) => void;
  onClose: () => void;
}> = ({ initial, onSave, onClose }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.documents;
  const C = M.common;
  const [form, setForm] = useState<Deliverable>(initial || {
    title: '', description: '', category: '', deadline: '', status: L.statusDefault, sections: [],
  });
  const [newSectionTitle, setNewSectionTitle] = useState('');

  const addSection = () => {
    const t = newSectionTitle.trim();
    if (!t) return;
    setForm(f => ({ ...f, sections: [...f.sections, { id: `s${Date.now()}`, title: t, content: '', subsections: [] }] }));
    setNewSectionTitle('');
  };

  const removeSection = (id: string) => setForm(f => ({ ...f, sections: f.sections.filter(s => s.id !== id) }));

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{initial ? L.modalEditDel : L.modalNewDel}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{L.fieldTitle}</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box' }}
              placeholder={L.fieldTitlePlaceholder} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{L.fieldDescription}</label>
            <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{L.fieldCategory}</label>
              <input value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{L.fieldDeadline}</label>
              <input type="date" value={form.deadline || ''} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{L.fieldStatus}</label>
              <select value={form.status || L.statusDefault} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box' }}>
                {L.statusOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 8 }}>{L.fieldChapters}</label>
            {form.sections.map((s, i) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 20 }}>{i + 1}.</span>
                <span style={{ flex: 1, fontSize: 13, color: '#374151', backgroundColor: '#f9fafb', padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb' }}>{s.title}</span>
                <button onClick={() => removeSection(s.id)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', width: 26, height: 26, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input value={newSectionTitle} onChange={e => setNewSectionTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addSection()}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
                placeholder={L.chapterPlaceholder} />
              <button onClick={addSection} style={{ padding: '8px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{L.addChapter}</button>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 28, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280', backgroundColor: 'white' }}>{C.cancel}</button>
          <button onClick={() => { if (form.title.trim()) onSave(form); }} disabled={!form.title.trim()}
            style={{ padding: '9px 24px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: form.title.trim() ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600, opacity: form.title.trim() ? 1 : 0.5 }}>
            {initial ? C.save : L.createBtn}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Delete Confirm Modal ──────────────────────────────────────
const DeleteConfirmModal: React.FC<{
  title: string;
  subtitle: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}> = ({ title, subtitle, onConfirm, onCancel, loading }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.documents;
  const C = M.common;
  const [typed, setTyped] = useState('');
  const canConfirm = typed === L.deleteConfirmWord;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 32, width: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
          </div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e', margin: '0 0 4px' }}>{title}</p>
            <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>{subtitle}</p>
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 500, display: 'block', marginBottom: 8 }}>
            {L.deleteConfirmLabelPre} <strong>{L.deleteConfirmWord}</strong> {L.deleteConfirmLabelPost}
          </label>
          <input
            autoFocus
            value={typed}
            onChange={e => setTyped(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canConfirm) onConfirm(); if (e.key === 'Escape') onCancel(); }}
            style={{ width: '100%', border: `1.5px solid ${canConfirm ? '#ef4444' : '#e5e7eb'}`, borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
            placeholder={L.deletePlaceholder}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={loading} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>{C.cancel}</button>
          <button onClick={onConfirm} disabled={!canConfirm || loading} style={{ backgroundColor: canConfirm ? '#fee2e2' : '#f3f4f6', color: canConfirm ? '#991b1b' : '#9ca3af', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: canConfirm ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}>
            {loading ? `${L.deleteConfirmWord}...` : L.deleteConfirmWord}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const DocumentsModule: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.documents;
  const C = M.common;
  const [activeTab, setActiveTab] = useState<'deliverables' | 'other'>('deliverables');
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [otherDocs, setOtherDocs] = useState<OtherDoc[]>([]);
  const [projectDocs, setProjectDocs] = useState<{ id: string; file_name: string; document_type: string; created_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('Άλλα');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Editor state
  const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null);
  const [editSections, setEditSections] = useState<Section[]>([]);
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [fillingItem, setFillingItem] = useState<string | null>(null); // key = sectionId or sectionId+subsectionId

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editModalDeliverable, setEditModalDeliverable] = useState<Deliverable | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'deliverable' | 'doc'; id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Inline sidebar inputs
  const [addingSectionTo, setAddingSectionTo] = useState<string | null>(null); // sectionId for subsection, 'root' for new section
  const [inlineInput, setInlineInput] = useState('');

  // Inline rename
  const [renamingItem, setRenamingItem] = useState<{ sectionId: string; subsectionId?: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const startRename = (sectionId: string, subsectionId?: string) => {
    const sec = editSections.find(s => s.id === sectionId);
    const title = subsectionId ? sec?.subsections?.find(ss => ss.id === subsectionId)?.title : sec?.title;
    setRenamingItem({ sectionId, subsectionId });
    setRenameValue(title || '');
  };

  const commitRename = () => {
    if (!renamingItem || !renameValue.trim()) { setRenamingItem(null); return; }
    const { sectionId, subsectionId } = renamingItem;
    setEditSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      if (subsectionId) {
        return { ...s, subsections: s.subsections.map(ss => ss.id === subsectionId ? { ...ss, title: renameValue.trim() } : ss) };
      }
      return { ...s, title: renameValue.trim() };
    }));
    setRenamingItem(null);
  };

  const headers = { Authorization: `Bearer ${token}` };

  // ── TipTap editor ──
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: '',
    editable: canEdit,
  });

  // Helper: get content for currently selected item
  const getItemContent = useCallback((sections: Section[], item: SelectedItem | null): string => {
    if (!item) return '';
    const sec = sections.find(s => s.id === item.sectionId);
    if (!sec) return '';
    if (item.subsectionId) {
      return sec.subsections?.find(ss => ss.id === item.subsectionId)?.content || '';
    }
    return sec.content || '';
  }, []);

  // Helper: save HTML back into editSections
  const saveItemContent = useCallback((item: SelectedItem, html: string) => {
    setEditSections(prev => prev.map(s => {
      if (s.id !== item.sectionId) return s;
      if (item.subsectionId) {
        return { ...s, subsections: (s.subsections || []).map(ss => ss.id === item.subsectionId ? { ...ss, content: html } : ss) };
      }
      return { ...s, content: html };
    }));
  }, []);

  // When selectedItem changes → flush old content, load new content
  const prevItem = useRef<SelectedItem | null>(null);
  useEffect(() => {
    if (!editor) return;
    // Flush previous item's content before switching
    if (prevItem.current && editor) {
      const html = editor.getHTML();
      saveItemContent(prevItem.current, html);
    }
    prevItem.current = selectedItem;
    // Load new item content
    const content = getItemContent(editSections, selectedItem);
    editor.commands.setContent(content || '', { emitUpdate: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  // Ref που κρατά πάντα την τελευταία έκδοση του saveDeliverable για χρήση σε cleanup
  const saveDeliverableRef = useRef<() => Promise<void>>(async () => {});

  // Auto-save when sections change (debounced 1.5s)
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const isSyncing = useRef(false);
  useEffect(() => {
    if (!selectedDeliverable?.id || editSections.length === 0 || isSyncing.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      isSyncing.current = true;
      await saveDeliverable();
      isSyncing.current = false;
    }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSections]);

  // Save on unmount (π.χ. ο χρήστης φεύγει μέσω breadcrumbs)
  useEffect(() => {
    saveDeliverableRef.current = saveDeliverable;
  });
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      saveDeliverableRef.current();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data fetching ──
  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [delRes, otherRes, projRes] = await Promise.all([
        axios.get(`${API}/documents-module/${project.id}/deliverables`, { headers }),
        axios.get(`${API}/documents-module/${project.id}/other-documents`, { headers }),
        axios.get(`${API}/documents/${project.id}`, { headers }),
      ]);
      setDeliverables(delRes.data || []);
      setOtherDocs(otherRes.data || []);
      setProjectDocs((projRes.data || []).filter((d: any) => d.document_type === 'contract'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openDeliverable = async (del: Deliverable) => {
    // Always fetch fresh from API to avoid stale state
    let fresh = del;
    try {
      const res = await axios.get(`${API}/documents-module/${project.id}/deliverables`, { headers });
      const found = (res.data || []).find((d: Deliverable) => d.id === del.id);
      if (found) fresh = found;
    } catch { }

    const normalizedSections: Section[] = (fresh.sections || []).map((s: any) => ({
      ...s,
      subsections: Array.isArray(s.subsections) ? s.subsections : [],
    }));
    prevItem.current = null;
    setSelectedDeliverable(fresh);
    setEditSections(normalizedSections);
    const firstItem = normalizedSections[0]
      ? { sectionId: normalizedSections[0].id }
      : null;
    setSelectedItem(firstItem);
    setSaveSuccess(false);
    setAddingSectionTo(null);
  };

  // Flush editor content into sections before save
  const flushEditor = (): Section[] => {
    if (!editor || !selectedItem) return editSections;
    const html = editor.getHTML();
    return editSections.map(s => {
      if (s.id !== selectedItem.sectionId) return s;
      if (selectedItem.subsectionId) {
        return { ...s, subsections: (s.subsections || []).map(ss => ss.id === selectedItem.subsectionId ? { ...ss, content: html } : ss) };
      }
      return { ...s, content: html };
    });
  };

  const saveDeliverable = async () => {
    if (!selectedDeliverable?.id) return;
    const finalSections = flushEditor();
    setEditSections(finalSections);
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/documents-module/${project.id}/deliverables/${selectedDeliverable.id}`,
        { ...selectedDeliverable, sections: finalSections, userName: user.full_name },
        { headers }
      );
      setDeliverables(prev => prev.map(d => d.id === selectedDeliverable.id ? res.data : d));
      setSelectedDeliverable(res.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  // ── AI fill for any item ──
  const handleAiFill = async (item: SelectedItem) => {
    const key = item.subsectionId ? `${item.sectionId}::${item.subsectionId}` : item.sectionId;
    setFillingItem(key);
    const section = editSections.find(s => s.id === item.sectionId);
    const subsection = item.subsectionId ? section?.subsections?.find(ss => ss.id === item.subsectionId) : null;
    const sectionTitle = section?.title || '';
    const itemTitle = subsection ? subsection.title : sectionTitle;
    const currentContent = subsection ? subsection.content : (section?.content || '');

    try {
      const res = await axios.post(
        `${API}/documents-module/${project.id}/fill-section`,
        {
          sectionTitle: subsection ? `${sectionTitle} > ${itemTitle}` : itemTitle,
          sectionContent: currentContent,
          deliverableTitle: selectedDeliverable?.title || '',
          deliverableDescription: selectedDeliverable?.description || '',
        },
        { headers }
      );
      const newContent = `<p>${(res.data.content || '').replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
      // Update in editSections
      setEditSections(prev => prev.map(s => {
        if (s.id !== item.sectionId) return s;
        if (item.subsectionId) {
          return { ...s, subsections: (s.subsections || []).map(ss => ss.id === item.subsectionId ? { ...ss, content: newContent } : ss) };
        }
        return { ...s, content: newContent };
      }));
      // Also update editor if this is the active item
      if (editor && selectedItem?.sectionId === item.sectionId && selectedItem?.subsectionId === item.subsectionId) {
        editor.commands.setContent(newContent, { emitUpdate: false });
      }
    } catch (err) { console.error(err); }
    finally { setFillingItem(null); }
  };

  // ── Deliverable CRUD ──
  const handleCreateDeliverable = async (form: Deliverable) => {
    try {
      const res = await axios.post(`${API}/documents-module/${project.id}/deliverables`, { ...form, userName: user.full_name }, { headers });
      setDeliverables(prev => [res.data, ...prev]);
      setShowCreateModal(false);
      openDeliverable(res.data);
    } catch (err) { console.error(err); }
  };

  const handleEditDeliverableSave = async (form: Deliverable) => {
    if (!form.id) return;
    const merged = form.sections.map(s => {
      const existing = editSections.find(e => e.id === s.id);
      return existing ? { ...s, content: existing.content, subsections: s.subsections.map(ss => {
        const existingSub = existing.subsections?.find(e => e.id === ss.id);
        return existingSub ? { ...ss, content: existingSub.content } : ss;
      }) } : { ...s, subsections: s.subsections || [] };
    });
    try {
      const res = await axios.put(`${API}/documents-module/${project.id}/deliverables/${form.id}`, { ...form, sections: merged, userName: user.full_name }, { headers });
      setDeliverables(prev => prev.map(d => d.id === form.id ? res.data : d));
      setSelectedDeliverable(res.data);
      setEditSections(merged);
      if (!selectedItem || !merged.find(s => s.id === selectedItem.sectionId)) {
        setSelectedItem(merged[0] ? { sectionId: merged[0].id } : null);
      }
      setEditModalDeliverable(null);
    } catch (err) { console.error(err); }
  };

  const handleDeleteDeliverable = async (id: string, title: string) => {
    setDeleteTarget({ type: 'deliverable', id, title });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'deliverable') {
        await axios.delete(`${API}/documents-module/${project.id}/deliverables/${deleteTarget.id}`, { headers });
        setDeliverables(prev => prev.filter(d => d.id !== deleteTarget.id));
        if (selectedDeliverable?.id === deleteTarget.id) setSelectedDeliverable(null);
      } else {
        await axios.delete(`${API}/documents-module/${project.id}/other-documents/${deleteTarget.id}`, { headers });
        setOtherDocs(prev => prev.filter(d => d.id !== deleteTarget.id));
      }
    } catch { }
    setDeleting(false);
    setDeleteTarget(null);
  };

  // ── Inline add section / subsection ──
  const commitInlineAdd = () => {
    const t = inlineInput.trim();
    if (!t) { setAddingSectionTo(null); setInlineInput(''); return; }
    if (addingSectionTo === 'root') {
      const newSec: Section = { id: `s${Date.now()}`, title: t, content: '', subsections: [] };
      setEditSections(prev => [...prev, newSec]);
      setSelectedItem({ sectionId: newSec.id });
    } else if (addingSectionTo) {
      const newSub: SubSection = { id: `ss${Date.now()}`, title: t, content: '' };
      setEditSections(prev => prev.map(s => s.id === addingSectionTo ? { ...s, subsections: [...(s.subsections || []), newSub] } : s));
      setSelectedItem({ sectionId: addingSectionTo, subsectionId: newSub.id });
    }
    setAddingSectionTo(null);
    setInlineInput('');
  };

  const removeSection = (sectionId: string) => {
    setEditSections(prev => prev.filter(s => s.id !== sectionId));
    if (selectedItem?.sectionId === sectionId) setSelectedItem(editSections.find(s => s.id !== sectionId) ? { sectionId: editSections.find(s => s.id !== sectionId)!.id } : null);
  };

  const removeSubSection = (sectionId: string, subId: string) => {
    setEditSections(prev => prev.map(s => s.id === sectionId ? { ...s, subsections: s.subsections.filter(ss => ss.id !== subId) } : s));
    if (selectedItem?.sectionId === sectionId && selectedItem?.subsectionId === subId) {
      setSelectedItem({ sectionId });
    }
  };

  // ── Other docs upload ──
  const handleUploadFile = async (file: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadCategory);
      formData.append('uploadedByName', user.full_name);
      const res = await axios.post(`${API}/documents-module/${project.id}/other-documents/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setOtherDocs(prev => [res.data.document, ...prev]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Upload failed';
      alert(`Σφάλμα upload: ${msg}`);
    }
    finally { setUploading(false); }
  };

  const viewDocument = async (id: string, fileName: string) => {
    try {
      const res = await axios.get(`${API}/documents-module/${project.id}/other-documents/${id}/signed-url`, { headers });
      window.open(res.data.url, '_blank');
    } catch { alert(L.viewDocError); }
  };

  const viewProjectDoc = async (id: string) => {
    try {
      const res = await axios.get(`${API}/documents/${project.id}/${id}/signed-url`, { headers });
      window.open(res.data.url, '_blank');
    } catch { alert(L.viewDocError); }
  };

  const handleDeleteOtherDoc = (id: string, fileName: string) => {
    setDeleteTarget({ type: 'doc', id, title: fileName });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const getFileIcon = (type?: string) => {
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    if (type?.includes('sheet') || type?.includes('excel')) return '📊';
    return '📎';
  };
  const statusColor = (s?: string) => {
    if (s === 'Completed') return { bg: '#dcfce7', text: '#166534' };
    if (s === 'In Progress') return { bg: '#dbeafe', text: '#1e40af' };
    return { bg: '#f3f4f6', text: '#6b7280' };
  };

  const activeItemKey = selectedItem ? (selectedItem.subsectionId ? `${selectedItem.sectionId}::${selectedItem.subsectionId}` : selectedItem.sectionId) : null;
  const isFillingActive = activeItemKey && fillingItem === activeItemKey;

  const getActiveLabel = () => {
    if (!selectedItem) return '';
    const sec = editSections.find(s => s.id === selectedItem.sectionId);
    if (!sec) return '';
    if (selectedItem.subsectionId) {
      const sub = sec.subsections?.find(ss => ss.id === selectedItem.subsectionId);
      return sub ? `${sec.title}  ›  ${sub.title}` : sec.title;
    }
    return sec.title;
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
      <p style={{ color: '#9ca3af', fontSize: 14 }}>{C.loading}</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{ padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: 14, position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: 500 }}>{L.pageTitle}</span>
      </div>

      {/* Tabs */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 32px', display: 'flex', alignItems: 'center' }}>
        {([
          { key: 'deliverables', label: L.tabDeliverables, count: deliverables.length },
          { key: 'other', label: L.tabOther, count: otherDocs.length },
        ] as { key: 'deliverables' | 'other'; label: string; count: number }[]).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '14px 20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            fontSize: 13, fontWeight: 500,
            color: activeTab === tab.key ? '#4f46e5' : '#6b7280',
            borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {tab.label}
            {tab.count > 0 && <span style={{ backgroundColor: activeTab === tab.key ? '#ede9fe' : '#f3f4f6', color: activeTab === tab.key ? '#4f46e5' : '#6b7280', padding: '1px 7px', borderRadius: 999, fontSize: 11 }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* ── TAB 1: ΠΑΡΑΔΟΤΕΑ — CARD LIST ── */}
      {activeTab === 'deliverables' && !selectedDeliverable && (
        <div style={{ padding: '32px 48px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{L.deliverablesTitle}</h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#9ca3af' }}>{L.deliverablesSubtitle}</p>
            </div>
            {canEdit && (
              <button onClick={() => setShowCreateModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {L.newDeliverable}
              </button>
            )}
          </div>

          {deliverables.length === 0 ? (
            <div style={{ backgroundColor: 'white', borderRadius: 12, padding: '80px 32px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
              <p style={{ fontSize: 16, color: '#374151', fontWeight: 600, margin: '0 0 8px' }}>{L.emptyDelTitle}</p>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>{L.emptyDelDesc}</p>
              {canEdit && <button onClick={() => setShowCreateModal(true)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>{L.createDeliverable}</button>}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
              {deliverables.map(del => {
                const sc = statusColor(del.status);
                const allItems = (del.sections || []).reduce((acc, s) => acc + 1 + (s.subsections?.length || 0), 0);
                const filledItems = (del.sections || []).reduce((acc, s) => {
                  const secFilled = s.content?.trim() ? 1 : 0;
                  const subFilled = (s.subsections || []).filter(ss => ss.content?.trim()).length;
                  return acc + secFilled + subFilled;
                }, 0);
                const pct = allItems > 0 ? Math.round((filledItems / allItems) * 100) : 0;
                return (
                  <div key={del.id} onClick={() => openDeliverable(del)}
                    style={{ backgroundColor: 'white', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', border: '1px solid #f1f5f9', transition: 'all 0.15s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(79,70,229,0.12)'; (e.currentTarget as HTMLElement).style.borderColor = '#c4b5fd'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLElement).style.borderColor = '#f1f5f9'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{del.title}</div>
                        {del.description && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{del.description}</div>}
                      </div>
                      <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, fontWeight: 600, backgroundColor: sc.bg, color: sc.text, marginLeft: 10, flexShrink: 0 }}>{del.status || L.statusDefault}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12, color: '#6b7280' }}>
                      {del.category && <span>📁 {del.category}</span>}
                      {del.deadline && <span>📅 {new Date(del.deadline).toLocaleDateString(C.dateLocale)}</span>}
                      <span>📝 {L.chaptersLabel((del.sections || []).length)}</span>
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button
                        onClick={e => { e.stopPropagation(); generateDeliverableDocx({ ...del, _locale: C.dateLocale, _catLabel: L.fieldCategory, _deadlineLabel: L.fieldDeadline }); }}
                        style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#374151', padding: '4px 10px', fontWeight: 500 }}>
                        ⬇ .docx
                      </button>
                      {canEdit && (
                        <button onClick={e => { e.stopPropagation(); handleDeleteDeliverable(del.id!, del.title); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#ef4444', padding: '2px 6px' }}>{L.deleteLabel}</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── DELIVERABLE EDITOR ── */}
      {activeTab === 'deliverables' && selectedDeliverable && (
        <div style={{ display: 'flex', height: 'calc(100vh - 120px)' }}>

          {/* ── Sidebar ── */}
          <div style={{ width: 280, backgroundColor: 'white', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <button onClick={async () => { await saveDeliverable(); setSelectedDeliverable(null); }} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280', padding: 0 }}>
                  {L.backBtn}
                </button>
                {canEdit && (
                  <button onClick={() => setEditModalDeliverable({ ...selectedDeliverable, sections: editSections })}
                    style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', color: '#374151', fontWeight: 500 }}>
                    {L.editBtn}
                  </button>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.4 }}>{selectedDeliverable.title}</div>
              {selectedDeliverable.category && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{selectedDeliverable.category}</div>}
            </div>

            {/* Section tree */}
            <div style={{ flex: 1, padding: '8px 8px' }}>
              {editSections.length === 0 && addingSectionTo !== 'root' && (
                <div style={{ padding: '16px 8px', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                  {L.noChapters}<br />
                  {canEdit && <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }} onClick={() => { setAddingSectionTo('root'); setInlineInput(''); }}>{L.addChapterLink}</span>}
                </div>
              )}

              {editSections.map((sec, si) => {
                const secSelected = selectedItem?.sectionId === sec.id && !selectedItem?.subsectionId;
                const secFilled = sec.content?.trim().length > 0;
                const secFillingKey = sec.id;

                return (
                  <div key={sec.id} style={{ marginBottom: 4 }}>
                    {/* Chapter row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      {renamingItem?.sectionId === sec.id && !renamingItem?.subsectionId ? (
                        <div style={{ flex: 1, display: 'flex', gap: 3, padding: '4px 4px' }}>
                          <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingItem(null); }}
                            onBlur={commitRename}
                            style={{ flex: 1, border: '1px solid #c4b5fd', borderRadius: 6, padding: '5px 7px', fontSize: 12, fontWeight: 600, outline: 'none' }} />
                        </div>
                      ) : (
                        <button onClick={() => setSelectedItem({ sectionId: sec.id })}
                          onDoubleClick={() => canEdit && startRename(sec.id)}
                          title={canEdit ? L.renameTip : undefined}
                          style={{
                            flex: 1, textAlign: 'left', padding: '8px 10px', borderRadius: 7,
                            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            backgroundColor: secSelected ? '#ede9fe' : 'transparent',
                            color: secSelected ? '#4f46e5' : '#1a1a2e',
                          }}>
                          <span style={{ fontSize: 10, color: secSelected ? '#6d28d9' : '#9ca3af', minWidth: 16, fontWeight: 700 }}>{si + 1}.</span>
                          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{sec.title}</span>
                          <span style={{ fontSize: 9, color: secFilled ? '#22c55e' : '#e5e7eb', flexShrink: 0 }}>●</span>
                        </button>
                      )}
                      {canEdit && (
                        <>
                          <button onClick={() => handleAiFill({ sectionId: sec.id })}
                            disabled={fillingItem === secFillingKey}
                            title={L.fillWithAITitle}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: fillingItem === secFillingKey ? '#c4b5fd' : '#9ca3af', padding: '4px', borderRadius: 4, flexShrink: 0 }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#6d28d9')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}>
                            ✦
                          </button>
                          <button onClick={() => removeSection(sec.id)}
                            title="Διαγραφή"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#d1d5db', padding: '4px', borderRadius: 4, flexShrink: 0 }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#d1d5db')}>
                            ✕
                          </button>
                        </>
                      )}
                    </div>

                    {/* Subsections */}
                    <div style={{ paddingLeft: 20 }}>
                      {(sec.subsections || []).map((sub, ssi) => {
                        const subSelected = selectedItem?.sectionId === sec.id && selectedItem?.subsectionId === sub.id;
                        const subFilled = sub.content?.trim().length > 0;
                        const subFillingKey = `${sec.id}::${sub.id}`;
                        return (
                          <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 1 }}>
                            {renamingItem?.sectionId === sec.id && renamingItem?.subsectionId === sub.id ? (
                              <div style={{ flex: 1, display: 'flex', gap: 3, padding: '3px 4px' }}>
                                <span style={{ fontSize: 9, color: '#c4b5fd', minWidth: 14, paddingTop: 6 }}>└─</span>
                                <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingItem(null); }}
                                  onBlur={commitRename}
                                  style={{ flex: 1, border: '1px solid #c4b5fd', borderRadius: 5, padding: '4px 6px', fontSize: 11, outline: 'none' }} />
                              </div>
                            ) : (
                            <button onClick={() => setSelectedItem({ sectionId: sec.id, subsectionId: sub.id })}
                              onDoubleClick={() => canEdit && startRename(sec.id, sub.id)}
                              title={canEdit ? L.renameTip : undefined}
                              style={{
                                flex: 1, textAlign: 'left', padding: '6px 8px', borderRadius: 6,
                                border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                                backgroundColor: subSelected ? '#ede9fe' : 'transparent',
                                color: subSelected ? '#4f46e5' : '#374151',
                              }}>
                              <span style={{ fontSize: 9, color: subSelected ? '#6d28d9' : '#c4b5fd', minWidth: 14 }}>└─</span>
                              <span style={{ fontSize: 11, fontWeight: subSelected ? 600 : 400, flex: 1, lineHeight: 1.3 }}>{ssi + 1}. {sub.title}</span>
                              <span style={{ fontSize: 8, color: subFilled ? '#22c55e' : '#e5e7eb', flexShrink: 0 }}>●</span>
                            </button>
                            )}
                            {canEdit && (
                              <>
                                <button onClick={() => handleAiFill({ sectionId: sec.id, subsectionId: sub.id })}
                                  disabled={fillingItem === subFillingKey}
                                  title={L.fillSubWithAITitle}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: fillingItem === subFillingKey ? '#c4b5fd' : '#9ca3af', padding: '3px', borderRadius: 4, flexShrink: 0 }}
                                  onMouseEnter={e => (e.currentTarget.style.color = '#6d28d9')}
                                  onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}>
                                  ✦
                                </button>
                                <button onClick={() => removeSubSection(sec.id, sub.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#d1d5db', padding: '3px', borderRadius: 4, flexShrink: 0 }}
                                  onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                                  onMouseLeave={e => (e.currentTarget.style.color = '#d1d5db')}>
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}

                      {/* Add subsection inline */}
                      {canEdit && addingSectionTo === sec.id && (
                        <div style={{ display: 'flex', gap: 4, padding: '4px 0', alignItems: 'center' }}>
                          <input autoFocus value={inlineInput} onChange={e => setInlineInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') commitInlineAdd(); if (e.key === 'Escape') { setAddingSectionTo(null); setInlineInput(''); } }}
                            placeholder={L.subChapterPlaceholder}
                            style={{ flex: 1, border: '1px solid #c4b5fd', borderRadius: 5, padding: '5px 7px', fontSize: 11, outline: 'none' }} />
                          <button onClick={commitInlineAdd} style={{ background: '#4f46e5', color: 'white', border: 'none', borderRadius: 5, padding: '5px 7px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                        </div>
                      )}
                      {canEdit && addingSectionTo !== sec.id && (
                        <button onClick={() => { setAddingSectionTo(sec.id); setInlineInput(''); }}
                          style={{ width: '100%', textAlign: 'left', padding: '5px 8px', border: '1px dashed #e5e7eb', borderRadius: 6, background: 'none', cursor: 'pointer', fontSize: 11, color: '#c4b5fd', marginTop: 3 }}>
                          {L.addSubChapter}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Add root section */}
              {canEdit && addingSectionTo === 'root' && (
                <div style={{ display: 'flex', gap: 4, padding: '6px 4px', alignItems: 'center' }}>
                  <input autoFocus value={inlineInput} onChange={e => setInlineInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitInlineAdd(); if (e.key === 'Escape') { setAddingSectionTo(null); setInlineInput(''); } }}
                    placeholder={L.chapterPlaceholder}
                    style={{ flex: 1, border: '1px solid #c4b5fd', borderRadius: 6, padding: '6px 8px', fontSize: 12, outline: 'none' }} />
                  <button onClick={commitInlineAdd} style={{ background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', fontSize: 12 }}>✓</button>
                </div>
              )}
              {canEdit && addingSectionTo !== 'root' && editSections.length > 0 && (
                <button onClick={() => { setAddingSectionTo('root'); setInlineInput(''); }}
                  style={{ width: '100%', textAlign: 'left', padding: '7px 10px', border: '1px dashed #e5e7eb', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                  {L.newChapterBtn}
                </button>
              )}
            </div>

            {/* Save button */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', position: 'sticky', bottom: 0, backgroundColor: 'white' }}>
              <button onClick={saveDeliverable} disabled={saving} style={{
                width: '100%', padding: '9px', borderRadius: 8, border: 'none',
                backgroundColor: saveSuccess ? '#22c55e' : '#4f46e5',
                color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600, transition: 'background-color 0.2s',
              }}>
                {saving ? L.savingBtn : saveSuccess ? L.savedBtn : C.save}
              </button>
            </div>
          </div>

          {/* ── Main editor ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'white' }}>
            {selectedItem ? (
              <>
                {/* Editor header */}
                <div style={{ padding: '14px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e', flex: 1 }}>{getActiveLabel()}</h2>
                  <button
                    onClick={async () => {
                      const finalSections = flushEditor();
                      await generateDeliverableDocx({ ...selectedDeliverable, sections: finalSections, _locale: C.dateLocale, _catLabel: L.fieldCategory, _deadlineLabel: L.fieldDeadline });
                    }}
                    title="Λήψη ολόκληρου του παραδοτέου σε μορφή Word"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', borderRadius: 8,
                      border: '1px solid #e5e7eb', cursor: 'pointer',
                      backgroundColor: 'white', color: '#374151',
                      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>
                    {L.downloadDocx}
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => handleAiFill(selectedItem)}
                      disabled={!!isFillingActive}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '7px 16px', borderRadius: 8,
                        border: '1px solid #c4b5fd', cursor: isFillingActive ? 'not-allowed' : 'pointer',
                        backgroundColor: isFillingActive ? '#f3f4f6' : '#ede9fe',
                        color: '#6d28d9', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                      {isFillingActive ? L.fillActiveBtn : L.fillBtn}
                    </button>
                  )}
                </div>

                {isFillingActive && (
                  <div style={{ padding: '10px 32px', backgroundColor: '#ede9fe', fontSize: 13, color: '#6d28d9', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {L.aiFillingNote}
                  </div>
                )}

                {/* TipTap editor box */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <Toolbar editor={editor} />
                      <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
                        <style>{`
                          .ProseMirror { outline: none; font-size: 14px; line-height: 1.8; color: #1a1a2e; min-height: 100%; }
                          .ProseMirror p { margin: 0 0 12px; }
                          .ProseMirror h1 { font-size: 22px; font-weight: 700; margin: 20px 0 10px; color: #1a1a2e; }
                          .ProseMirror h2 { font-size: 18px; font-weight: 700; margin: 16px 0 8px; color: #1a1a2e; }
                          .ProseMirror h3 { font-size: 15px; font-weight: 600; margin: 14px 0 6px; color: #374151; }
                          .ProseMirror ul { padding-left: 24px; margin: 0 0 12px; }
                          .ProseMirror ol { padding-left: 24px; margin: 0 0 12px; }
                          .ProseMirror li { margin-bottom: 4px; }
                          .ProseMirror blockquote { border-left: 3px solid #c4b5fd; padding-left: 16px; color: #6b7280; margin: 12px 0; }
                          .ProseMirror code { background: #f1f5f9; padding: 1px 5px; border-radius: 4px; font-size: 13px; }
                          .ProseMirror pre { background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; overflow-x: auto; margin: 12px 0; }
                          .ProseMirror pre code { background: none; padding: 0; color: inherit; }
                          .ProseMirror.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; height: 0; float: left; }
                          .tiptap-editor-wrap { display: flex; flex-direction: column; flex: 1; height: 100%; }
                          .tiptap-editor-wrap .ProseMirror { flex: 1; }
                        `}</style>
                        <EditorContent editor={editor} className="tiptap-editor-wrap" />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }}>
                {L.selectChapter}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: ΒΑΣΗ ΓΝΩΣΗΣ ΕΡΓΟΥ ── */}
      {activeTab === 'other' && (() => {
        const categories = L.categories;
        const knownCats = categories.map(c => c.id);

        const DocRow = ({ doc }: { doc: OtherDoc }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderTop: '1px solid #f9fafb' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, backgroundColor: doc.has_knowledge ? '#ede9fe' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              {getFileIcon(doc.file_type)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                {[formatFileSize(doc.file_size), new Date(doc.created_at).toLocaleDateString(C.dateLocale), doc.uploaded_by_name].filter(Boolean).join(' · ')}
              </div>
            </div>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, flexShrink: 0, backgroundColor: doc.has_knowledge ? '#dcfce7' : '#f3f4f6', color: doc.has_knowledge ? '#16a34a' : '#9ca3af' }}>
              {doc.has_knowledge ? L.docAnalysed : L.docFile}
            </span>
            <button onClick={() => viewDocument(doc.id, doc.file_name)}
              title="Προβολή αρχείου"
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 7, backgroundColor: 'white', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 500, flexShrink: 0 }}>
              {L.viewDoc}
            </button>
            {canEdit && (
              <button onClick={() => handleDeleteOtherDoc(doc.id, doc.file_name)}
                title="Διαγραφή"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#d1d5db', padding: '4px', borderRadius: 6, flexShrink: 0 }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                onMouseLeave={e => (e.currentTarget.style.color = '#d1d5db')}>🗑</button>
            )}
          </div>
        );
        return (
          <div style={{ padding: '32px 48px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 28, alignItems: 'start' }}>

              {/* ── LEFT: Upload panel ── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {canEdit && (
                  <div style={{ backgroundColor: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>{L.addDocument}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{L.addDocumentHint}</div>
                    </div>
                    <div style={{ padding: '12px 20px' }}>
                      <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleUploadFile(f); }}
                        onClick={() => fileInputRef.current?.click()}
                        style={{ border: `2px dashed ${dragOver ? '#4f46e5' : '#e5e7eb'}`, borderRadius: 10, padding: '20px 12px', textAlign: 'center', cursor: 'pointer', backgroundColor: dragOver ? '#ede9fe' : '#fafafa', transition: 'all 0.2s' }}>
                        <input ref={fileInputRef} type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                          onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadFile(f); }} />
                        {uploading
                          ? <div><div style={{ fontSize: 24, marginBottom: 6 }}>⏳</div><div style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600 }}>{L.uploadingHint}</div></div>
                          : <div><div style={{ fontSize: 24, marginBottom: 6 }}>📂</div><div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{L.uploadHint}</div><div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>PDF, DOCX, XLSX, TXT</div></div>}
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {/* ── RIGHT: Documents list ── */}
              <div>

                {otherDocs.length === 0 && projectDocs.length === 0 ? (
                  <div style={{ backgroundColor: 'white', borderRadius: 14, padding: '64px 32px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 48, marginBottom: 14 }}>🧠</div>
                    <p style={{ fontSize: 15, color: '#374151', fontWeight: 600, margin: '0 0 8px' }}>{L.knowledgeBaseEmpty}</p>
                    <p style={{ fontSize: 13, color: '#9ca3af', margin: 0, lineHeight: 1.6 }}>{L.knowledgeBaseEmptyDesc}</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {knownCats.map(cat => {
                      const docs = otherDocs.filter(d => d.category === cat);
                      const isPrimaryContract = cat === 'Σύμβαση';
                      if (docs.length === 0 && !(isPrimaryContract && projectDocs.length > 0)) return null;
                      const catInfo = categories.find(c => c.id === cat)!;
                      const totalCount = docs.length + (isPrimaryContract ? projectDocs.length : 0);
                      return (
                        <div key={cat} style={{ backgroundColor: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                          <div style={{ padding: '10px 20px', backgroundColor: '#fafafa', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14 }}>{catInfo.icon}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{catInfo.label}</span>
                            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 2 }}>{L.filesCount(totalCount)}</span>
                          </div>
                          {isPrimaryContract && projectDocs.map(doc => {
                            let displayName = doc.file_name;
                            try { displayName = decodeURIComponent(escape(doc.file_name)); } catch { }
                            return (
                            <div key={`proj-${doc.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderTop: '1px solid #f9fafb' }}>
                              <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, backgroundColor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                                📄
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{new Date(doc.created_at).toLocaleDateString(C.dateLocale)}</div>
                              </div>
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, flexShrink: 0, backgroundColor: '#ede9fe', color: '#6d28d9' }}>
                                {language === 'en' ? 'Original' : 'Αρχικό'}
                              </span>
                              <button onClick={() => viewProjectDoc(doc.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 7, backgroundColor: 'white', cursor: 'pointer', fontSize: 12, color: '#374151', fontWeight: 500, flexShrink: 0 }}>
                                {L.viewDoc}
                              </button>
                            </div>
                          );})}
                          {docs.map(doc => <DocRow key={doc.id} doc={doc} />)}
                        </div>
                      );
                    })}
                    {otherDocs.filter(d => !knownCats.includes(d.category || '')).length > 0 && (
                      <div style={{ backgroundColor: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                        <div style={{ padding: '10px 20px', backgroundColor: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{categories.find(c => c.id === 'Άλλα')?.label ?? 'Other'}</span>
                        </div>
                        {otherDocs.filter(d => !knownCats.includes(d.category || '')).map(doc => <DocRow key={doc.id} doc={doc} />)}
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {showCreateModal && <DeliverableModal onSave={handleCreateDeliverable} onClose={() => setShowCreateModal(false)} />}
      {editModalDeliverable && <DeliverableModal initial={editModalDeliverable} onSave={handleEditDeliverableSave} onClose={() => setEditModalDeliverable(null)} />}
      {deleteTarget && (
        <DeleteConfirmModal
          title={deleteTarget.type === 'deliverable' ? L.deleteDeliverableTitle : L.deleteDocTitle}
          subtitle={L.deleteSubtitle(deleteTarget.title)}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default DocumentsModule;
